import { create } from 'zustand';
import { logger } from '@/services/LoggingService';

interface SystemState {
  // Storage capability
  storageBlocked: boolean;
  
  // Environment detection
  isPreviewEnvironment: boolean;
  
  // Network status
  isOffline: boolean;
  
  // Actions
  testStorageCapability: () => boolean;
  detectPreviewEnvironment: () => boolean;
  
  // Utilities
  setStorageBlocked: (blocked: boolean) => void;
  setOffline: (offline: boolean) => void;
}

export const useSystemStore = create<SystemState>((set, get) => ({
  // Initial state
  storageBlocked: false,
  isPreviewEnvironment: false,
  isOffline: false,

  // Test browser storage capability
  testStorageCapability: (): boolean => {
    try {
      const testKey = '__test_storage__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      
      set({ storageBlocked: false });
      return true;
    } catch (error) {
      logger.error('SystemStore', 'Storage capability test failed', { error });
      set({ storageBlocked: true });
      return false;
    }
  },

  // Detect if we're in preview environment
  detectPreviewEnvironment: (): boolean => {
    const isPreview = window.location.hostname.includes('lovable') || 
                     window.location.hostname === 'localhost';
    
    set({ isPreviewEnvironment: isPreview });
    return isPreview;
  },

  // Set storage blocked state
  setStorageBlocked: (blocked: boolean) => {
    set({ storageBlocked: blocked });
  },

  // Set offline/online state
  setOffline: (offline: boolean) => {
    // Only log meaningful transitions
    const prev = get().isOffline;
    if (prev !== offline) {
      if (offline) {
        logger.warn('SystemStore', 'Network status changed: OFFLINE');
      } else {
        logger.info('SystemStore', 'Network status changed: ONLINE');
      }
    }
    set({ isOffline: offline });
  }
}));
